import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, Reorder } from 'framer-motion';
import { PageTransition, FadeIn } from '../components/layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { 
  ChevronLeft, Plus, Pencil, Trash2, Star, GripVertical, 
  Loader2, Eye, EyeOff, Quote, AlertTriangle 
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Star selector component
const StarSelector = ({ value, onChange }) => {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          className="p-1 hover:scale-110 transition-transform"
        >
          <Star
            size={24}
            className={star <= value ? 'fill-[var(--gold)] text-[var(--gold)]' : 'text-gray-300 hover:text-[var(--gold)]'}
          />
        </button>
      ))}
    </div>
  );
};

// Review card for admin list
const ReviewCard = ({ review, onEdit, onDelete, onToggle }) => {
  const initials = review.name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <Reorder.Item
      value={review}
      id={review.id}
      className="bg-white border border-[var(--champagne)] p-4 cursor-grab active:cursor-grabbing"
      whileDrag={{ 
        scale: 1.02, 
        boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
        zIndex: 50
      }}
    >
      <div className="flex items-start gap-4">
        {/* Drag handle */}
        <div className="flex-shrink-0 pt-1 text-[var(--charcoal)]/30 hover:text-[var(--charcoal)]/60">
          <GripVertical size={20} />
        </div>

        {/* Avatar */}
        <div className="flex-shrink-0">
          {review.avatar_url ? (
            <img 
              src={review.avatar_url} 
              alt={review.name}
              className="w-12 h-12 rounded-full object-cover border border-[var(--champagne)]"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[var(--gold)]/20 to-[var(--dark-rosegold)]/20 flex items-center justify-center border border-[var(--champagne)]">
              <span className="text-sm font-medium text-[var(--dark-rosegold)]">
                {initials}
              </span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-medium text-[var(--charcoal)] truncate">
              {review.name}
            </h4>
            {review.source && (
              <span className="text-xs text-[var(--charcoal)]/50 bg-[var(--champagne)]/30 px-2 py-0.5 rounded">
                {review.source}
              </span>
            )}
            {!review.is_active && (
              <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded flex items-center gap-1">
                <EyeOff size={12} />
                Masqué
              </span>
            )}
          </div>
          
          <div className="flex gap-0.5 mb-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                size={12}
                className={star <= review.rating ? 'fill-[var(--gold)] text-[var(--gold)]' : 'text-gray-300'}
              />
            ))}
          </div>
          
          <p className="text-sm text-[var(--charcoal)]/70 line-clamp-2 italic">
            &ldquo;{review.comment}&rdquo;
          </p>
        </div>

        {/* Actions */}
        <div className="flex-shrink-0 flex items-center gap-2">
          <button
            onClick={() => onToggle(review)}
            className={`p-2 rounded-lg transition-colors ${
              review.is_active 
                ? 'text-green-600 hover:bg-green-50' 
                : 'text-gray-400 hover:bg-gray-100'
            }`}
            title={review.is_active ? 'Masquer' : 'Afficher'}
          >
            {review.is_active ? <Eye size={18} /> : <EyeOff size={18} />}
          </button>
          <button
            onClick={() => onEdit(review)}
            className="p-2 text-[var(--charcoal)]/60 hover:text-[var(--gold)] hover:bg-[var(--champagne)]/30 rounded-lg transition-colors"
            title="Modifier"
          >
            <Pencil size={18} />
          </button>
          <button
            onClick={() => onDelete(review)}
            className="p-2 text-[var(--charcoal)]/60 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            title="Supprimer"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>
    </Reorder.Item>
  );
};

export default function AdminReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [reviewToDelete, setReviewToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    rating: 5,
    comment: '',
    source: '',
    avatar_url: '',
    is_active: true,
    is_highlighted: false
  });

  const token = localStorage.getItem('dsrs_token');

  // Fetch reviews
  const fetchReviews = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/reviews`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviews(response.data.reviews || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      toast.error('Erreur lors du chargement des avis');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  // Open dialog for create/edit
  const openDialog = (review = null) => {
    if (review) {
      setEditingReview(review);
      setFormData({
        name: review.name,
        rating: review.rating,
        comment: review.comment,
        source: review.source || '',
        avatar_url: review.avatar_url || '',
        is_active: review.is_active,
        is_highlighted: review.is_highlighted
      });
    } else {
      setEditingReview(null);
      setFormData({
        name: '',
        rating: 5,
        comment: '',
        source: '',
        avatar_url: '',
        is_active: true,
        is_highlighted: false
      });
    }
    setIsDialogOpen(true);
  };

  // Save review (create or update)
  const handleSave = async () => {
    if (!formData.name.trim() || !formData.comment.trim()) {
      toast.error('Le nom et le commentaire sont requis');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        source: formData.source || null,
        avatar_url: formData.avatar_url || null
      };

      if (editingReview) {
        await axios.put(
          `${API_URL}/api/admin/reviews/${editingReview.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Avis modifié avec succès');
      } else {
        await axios.post(
          `${API_URL}/api/admin/reviews`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Avis créé avec succès');
      }

      setIsDialogOpen(false);
      fetchReviews();
    } catch (error) {
      console.error('Error saving review:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Toggle review visibility
  const handleToggle = async (review) => {
    try {
      await axios.patch(
        `${API_URL}/api/admin/reviews/${review.id}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(review.is_active ? 'Avis masqué' : 'Avis affiché');
      fetchReviews();
    } catch (error) {
      console.error('Error toggling review:', error);
      toast.error('Erreur lors de la modification');
    }
  };

  // Delete review
  const handleDelete = async () => {
    if (!reviewToDelete) return;

    try {
      await axios.delete(
        `${API_URL}/api/admin/reviews/${reviewToDelete.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Avis supprimé');
      setIsDeleteDialogOpen(false);
      setReviewToDelete(null);
      fetchReviews();
    } catch (error) {
      console.error('Error deleting review:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // Reorder reviews
  const handleReorder = async (newOrder) => {
    setReviews(newOrder);
    
    try {
      await axios.post(
        `${API_URL}/api/admin/reviews/reorder`,
        { review_ids: newOrder.map(r => r.id) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error reordering reviews:', error);
      toast.error('Erreur lors de la réorganisation');
      fetchReviews(); // Revert on error
    }
  };

  const activeCount = reviews.filter(r => r.is_active).length;

  return (
    <PageTransition>
      <section className="min-h-screen bg-gradient-to-b from-[var(--champagne)]/20 to-white py-12 px-6">
        <div className="max-w-[1000px] mx-auto">
          {/* Header */}
          <FadeIn>
            <Link 
              to="/admin" 
              className="inline-flex items-center gap-2 text-sm text-[var(--charcoal)]/60 hover:text-[var(--gold)] transition-colors mb-6"
            >
              <ChevronLeft size={16} />
              Retour au tableau de bord
            </Link>

            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-light text-[var(--charcoal)]">
                  Gestion des Avis
                </h1>
                <p className="text-sm text-[var(--charcoal)]/60 mt-2">
                  {reviews.length} avis • {activeCount} visible{activeCount > 1 ? 's' : ''} sur le site
                </p>
              </div>
              <Button
                onClick={() => openDialog()}
                className="bg-[var(--gold)] hover:bg-[var(--dark-gold)] text-white"
                data-testid="add-review-btn"
              >
                <Plus size={18} className="mr-2" />
                Ajouter un avis
              </Button>
            </div>
          </FadeIn>

          {/* Reviews list */}
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 size={32} className="animate-spin text-[var(--gold)]" />
            </div>
          ) : reviews.length === 0 ? (
            <FadeIn>
              <div className="text-center py-16 bg-white border border-[var(--champagne)]">
                <Quote size={48} className="mx-auto text-[var(--charcoal)]/20 mb-4" />
                <p className="text-[var(--charcoal)]/60 mb-4">
                  Aucun avis pour le moment
                </p>
                <Button
                  onClick={() => openDialog()}
                  variant="outline"
                  className="border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-white"
                >
                  <Plus size={18} className="mr-2" />
                  Créer le premier avis
                </Button>
              </div>
            </FadeIn>
          ) : (
            <FadeIn>
              <div className="bg-[var(--champagne)]/20 p-4 rounded-lg mb-4 flex items-center gap-3">
                <GripVertical size={18} className="text-[var(--charcoal)]/40" />
                <p className="text-sm text-[var(--charcoal)]/70">
                  Glissez-déposez les avis pour modifier leur ordre d'affichage
                </p>
              </div>
              <Reorder.Group
                axis="y"
                values={reviews}
                onReorder={handleReorder}
                className="space-y-3"
                data-testid="reviews-list"
              >
                {reviews.map((review) => (
                  <ReviewCard
                    key={review.id}
                    review={review}
                    onEdit={openDialog}
                    onDelete={(r) => {
                      setReviewToDelete(r);
                      setIsDeleteDialogOpen(true);
                    }}
                    onToggle={handleToggle}
                  />
                ))}
              </Reorder.Group>
            </FadeIn>
          )}
        </div>
      </section>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-['Cormorant_Garamond'] text-2xl">
              {editingReview ? 'Modifier l\'avis' : 'Nouvel avis'}
            </DialogTitle>
            <DialogDescription>
              {editingReview 
                ? 'Modifiez les informations de cet avis client.' 
                : 'Ajoutez un nouvel avis client à afficher sur le site.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name">Nom / Pseudo <span className="text-red-500">*</span></Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Sophie M."
                data-testid="review-name-input"
              />
            </div>

            {/* Rating */}
            <div className="space-y-2">
              <Label>Note <span className="text-red-500">*</span></Label>
              <StarSelector
                value={formData.rating}
                onChange={(rating) => setFormData({ ...formData, rating })}
              />
            </div>

            {/* Comment */}
            <div className="space-y-2">
              <Label htmlFor="comment">Commentaire <span className="text-red-500">*</span></Label>
              <Textarea
                id="comment"
                value={formData.comment}
                onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                placeholder="Un moment de détente absolue..."
                rows={4}
                data-testid="review-comment-input"
              />
            </div>

            {/* Source */}
            <div className="space-y-2">
              <Label htmlFor="source">Source (optionnel)</Label>
              <Select
                value={formData.source || 'none'}
                onValueChange={(value) => setFormData({ ...formData, source: value === 'none' ? '' : value })}
              >
                <SelectTrigger data-testid="review-source-select">
                  <SelectValue placeholder="Sélectionner une source" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucune</SelectItem>
                  <SelectItem value="Google">Google</SelectItem>
                  <SelectItem value="Planity">Planity</SelectItem>
                  <SelectItem value="Facebook">Facebook</SelectItem>
                  <SelectItem value="Instagram">Instagram</SelectItem>
                  <SelectItem value="Client direct">Client direct</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Avatar URL */}
            <div className="space-y-2">
              <Label htmlFor="avatar_url">URL de l'avatar (optionnel)</Label>
              <Input
                id="avatar_url"
                value={formData.avatar_url}
                onChange={(e) => setFormData({ ...formData, avatar_url: e.target.value })}
                placeholder="https://..."
              />
              <p className="text-xs text-[var(--charcoal)]/50">
                Laissez vide pour utiliser les initiales du nom
              </p>
            </div>

            {/* Active toggle */}
            <div className="flex items-center justify-between py-2">
              <div>
                <Label htmlFor="is_active">Visible sur le site</Label>
                <p className="text-xs text-[var(--charcoal)]/50">
                  Désactivez pour masquer temporairement cet avis
                </p>
              </div>
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDialogOpen(false)}
              disabled={saving}
            >
              Annuler
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[var(--gold)] hover:bg-[var(--dark-gold)] text-white"
              data-testid="save-review-btn"
            >
              {saving ? (
                <>
                  <Loader2 size={16} className="animate-spin mr-2" />
                  Enregistrement...
                </>
              ) : (
                editingReview ? 'Mettre à jour' : 'Créer'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle size={20} />
              Supprimer cet avis ?
            </DialogTitle>
            <DialogDescription>
              Cette action est irréversible. L'avis de "{reviewToDelete?.name}" sera définitivement supprimé.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Annuler
            </Button>
            <Button
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-btn"
            >
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageTransition>
  );
}
